import { useRef, useState } from "react";
import { Music2, Plus, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr, resolveMediaUrl } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";

export const defaultBackgroundJingle = () => ({
  enabled: false,
  volume: 60,
  playlist: [],
});

export function normalizeBackgroundJingle(value) {
  const source = value && typeof value === "object" ? value : {};
  const playlist = Array.isArray(source.playlist)
    ? source.playlist
        .filter((track) => track && track.id && track.url)
        .map((track, index) => ({
          id: String(track.id),
          title: track.title || track.name || `Jingle ${index + 1}`,
          url: track.url,
          order: Number(track.order || index + 1),
          active: track.active !== false,
          duration: Number(track.duration || 0),
          content_type: track.content_type || "",
        }))
        .sort((a, b) => a.order - b.order)
    : [];
  return {
    enabled: source.enabled === true,
    volume: Math.max(0, Math.min(100, Number(source.volume ?? 60))),
    playlist,
  };
}

function endpointFor(scope, deviceId, trackId = "") {
  const prefix = scope === "admin" ? "/admin" : scope === "dealer" ? "/dealer" : "/client";
  return `${prefix}/devices/${deviceId}/jingles${trackId ? `/${trackId}` : ""}`;
}

export default function BackgroundJingleSettings({ scope = "client", deviceId, value, onChange, disabled = false }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const jingle = normalizeBackgroundJingle(value);

  const update = (patch) => onChange?.(normalizeBackgroundJingle({ ...jingle, ...patch }));

  const updateTrack = (id, patch) => {
    update({
      playlist: jingle.playlist.map((track) => (track.id === id ? { ...track, ...patch } : track)),
    });
  };

  const uploadTrack = async (file) => {
    if (!file || !deviceId) return;
    const form = new FormData();
    form.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post(endpointFor(scope, deviceId), form);
      update(data.backgroundJingle || data);
      toast.success("Audio uploaded");
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const deleteTrack = async (id) => {
    if (!deviceId) {
      update({ playlist: jingle.playlist.filter((track) => track.id !== id) });
      return;
    }
    try {
      const { data } = await api.delete(endpointFor(scope, deviceId, id));
      update(data.backgroundJingle || { ...jingle, playlist: jingle.playlist.filter((track) => track.id !== id) });
      toast.success("Audio removed");
    } catch (err) {
      toast.error(formatErr(err));
    }
  };

  return (
    <section className="rounded-sm border border-[#E5E7EB] p-4 bg-white">
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-sm bg-[#111827] text-white flex items-center justify-center">
              <Music2 className="w-4 h-4" />
            </div>
            <div>
              <div className="font-semibold text-[#111827]">Background Jingle Music</div>
              <div className="text-xs text-[#6B7280]">When enabled, player video audio is muted and this playlist loops globally.</div>
            </div>
          </div>
          <Switch checked={jingle.enabled} disabled={disabled} onCheckedChange={(checked) => update({ enabled: checked === true })} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_auto] gap-4 items-end">
          <div>
            <Label className="text-xs uppercase tracking-wider text-[#6B7280]">Jingle Volume ({jingle.volume}%)</Label>
            <Input
              className="mt-2"
              type="range"
              min="0"
              max="100"
              disabled={disabled}
              value={jingle.volume}
              onChange={(event) => update({ volume: Number(event.target.value || 60) })}
            />
          </div>
          <div>
            <input
              ref={fileRef}
              type="file"
              accept=".mp3,.wav,.aac,.m4a,audio/mpeg,audio/wav,audio/aac,audio/mp4"
              className="hidden"
              onChange={(event) => uploadTrack(event.target.files?.[0])}
            />
            <Button type="button" className="w-full rounded-sm bg-[#111827] hover:bg-[#374151] lg:w-auto" disabled={disabled || uploading || !deviceId} onClick={() => fileRef.current?.click()}>
              {uploading ? <Upload className="w-4 h-4 mr-2 animate-pulse" /> : <Plus className="w-4 h-4 mr-2" />}
              Upload Audio
            </Button>
          </div>
        </div>

        <div className="rounded-sm border border-[#E5E7EB]">
          <div className="border-b border-[#E5E7EB] bg-[#F9FAFB] px-3 py-2 text-xs font-semibold uppercase tracking-wider text-[#6B7280]">
            Playlist
          </div>
          {jingle.playlist.length === 0 ? (
            <div className="p-6 text-center text-sm text-[#6B7280]">No jingle audio uploaded yet.</div>
          ) : (
            <div className="divide-y divide-[#E5E7EB]">
              {jingle.playlist.map((track) => (
                <div key={track.id} className="grid min-w-0 gap-3 p-3 lg:grid-cols-[minmax(180px,1fr)_70px_72px_90px_minmax(210px,260px)_40px] lg:items-center">
                  <div className="min-w-0">
                    <Label className="mb-1 block text-[10px] uppercase tracking-wider text-[#6B7280] lg:hidden">Track Name</Label>
                    <Input value={track.title} disabled={disabled} onChange={(event) => updateTrack(track.id, { title: event.target.value })} />
                  </div>
                  <div className="grid grid-cols-2 gap-3 lg:contents">
                    <div className="text-sm text-[#6B7280]">
                      <Label className="mb-1 block text-[10px] uppercase tracking-wider text-[#6B7280] lg:hidden">Duration</Label>
                      {track.duration ? `${Math.round(track.duration)}s` : "-"}
                    </div>
                    <div>
                      <Label className="mb-1 block text-[10px] uppercase tracking-wider text-[#6B7280] lg:hidden">Status</Label>
                      <Switch checked={track.active !== false} disabled={disabled} onCheckedChange={(checked) => updateTrack(track.id, { active: checked === true })} />
                    </div>
                  </div>
                  <div className="w-24 lg:w-auto">
                    <Label className="mb-1 block text-[10px] uppercase tracking-wider text-[#6B7280] lg:hidden">Order</Label>
                    <Input type="number" min="1" value={track.order} disabled={disabled} onChange={(event) => updateTrack(track.id, { order: Number(event.target.value || 1) })} />
                  </div>
                  <audio controls preload="none" src={resolveMediaUrl(track.url)} className="w-full max-w-full" />
                  <Button type="button" variant="ghost" size="icon" disabled={disabled} onClick={() => deleteTrack(track.id)} aria-label="Delete audio track">
                    <Trash2 className="w-4 h-4 text-red-600" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
